import React from 'react'

import { Badge, Button, Card, Col, Row, Spinner } from 'react-bootstrap'
import Select from 'react-select'

import * as Request from '../../utilities/request'
import * as Helpers from './helpers'

import { AppContext } from '../../App'
import { Loading } from '../../components/ui/Loading/Loading'
import { BillyTrackingCategorySettings, BillyXeroOrgSettings } from '../../../../back-end/common/account'
import { XeroAccount } from '../../../../back-end/common/xeroAccount'
import XeroDisconnect from '../../images/disconnect-white.svg'
import { XeroTrackingCategory } from '../../../../back-end/common/xeroTrackingCategories'
import { Staff } from '../../../../back-end/common/staff'

interface OrgCardProps {
	org: Helpers.Org
	disconnecting: boolean
	updateShowDialog: (x: string) => void
	consentUrl: string
	accounts: XeroAccount[]
	trackingCategories: XeroTrackingCategory[]
	directors: Staff[]
	handleUpdateAccount: (column: string, value: string) => void
}

const AdvancedOrgCard = (props: OrgCardProps) => {
	const { appState } = React.useContext(AppContext)
	const accountsLoading = appState.accounts.length === 0

	if (accountsLoading) {
		return (
			<Card className="center-flex p-5">
				<Loading show />
			</Card>
		)
	}

	const currentAccountBillySettings = appState.accounts.find(
		(acc) => acc.accountID === appState.authState.attributes?.['custom:currentAccountId']
	)?.billySettings
	if (!currentAccountBillySettings) {
		return (
			<Card className="center-flex p-5">
				<span>No Billy settings found for this account.</span>
			</Card>
		)
	}
	const currentOrg = currentAccountBillySettings.orgSettings.find((os) => os.xeroOrgID === props.org.xeroOrgID)
	if (!currentOrg) {
		return (
			<Card className="center-flex p-5">
				<span>No Billy settings found for this org.</span>
			</Card>
		)
	}

	const [serviceAccount, setServiceAccount] = React.useState(currentOrg.servicesXeroAccountXeroID)
	const [estimateAccount, setEstimateAccount] = React.useState(currentOrg.estimatesXeroAccountXeroID)
	const [costAccount, setCostAccount] = React.useState(currentOrg.costsXeroAccountXeroID)
	const [trackingCategory, setTrackingCategory] = React.useState<string>(currentOrg.invoiceXeroTrackingCategoryID)
	const [staffTrackingCategory, setStaffTrackingCategory] = React.useState<BillyTrackingCategorySettings[]>(currentOrg.staffTrackingCategoryOptions)
	const [editing, setEditing] = React.useState<boolean>(false)
	const [submitting, setSubmitting] = React.useState<boolean>(false)
	const accountOptions = React.useMemo(() => props.accounts.map((acc) => ({ label: acc.name!, value: acc.xeroID! })), [props.accounts])
	const trackingCategories = React.useMemo(
		() => props.trackingCategories.map((dir) => ({ label: dir.name, value: dir.xeroTrackingCategoryID })),
		[props.trackingCategories]
	)
	const trackingCategoryOptions = React.useMemo(
		() =>
			props.trackingCategories
				.find((tc) => tc.xeroTrackingCategoryID === trackingCategory)
				?.options.map((opt) => ({ label: opt.name, value: opt.xeroTrackingCategoryOptionID })),
		[props.trackingCategories, trackingCategory]
	)

	const handleSave = async () => {
		setSubmitting(true)
		await Request.put('billySettings/org', {
			billySettingsXeroAccountID: currentOrg.billySettingsXeroAccountID,
			xeroOrgID: currentOrg.xeroOrgID,
			servicesXeroAccountXeroID: serviceAccount,
			estimatesXeroAccountXeroID: estimateAccount,
			costsXeroAccountXeroID: costAccount,
			invoiceXeroTrackingCategoryID: trackingCategory,
			staffTrackingCategoryOptions: staffTrackingCategory,
		} as Partial<BillyXeroOrgSettings>)
		setSubmitting(false)
		setEditing(false)
	}
	const handleCancel = async () => {
		setEditing(false)
	}

	return (
		<Card className="p-3">
			<OrgCard {...props} />
			{props.accounts.length > 0 && (
				<>
					{editing && (
						<Row className="mt-2">
							<Col>
								<h4>Accounts</h4>
								<label>Service Account</label>
								<Select
									isDisabled={!editing || submitting}
									options={accountOptions}
									value={accountOptions.find((acc) => acc.value === serviceAccount)}
									onChange={(option) => option && setServiceAccount(option.value)}
									className="mb-3"
								/>
								<label>Estimate Account</label>
								<Select
									isDisabled={!editing || submitting}
									options={accountOptions}
									value={accountOptions.find((acc) => acc.value === estimateAccount)}
									onChange={(option) => option && setEstimateAccount(option.value)}
									className="mb-3"
								/>
								<label>Cost Account</label>
								<Select
									isDisabled={!editing || submitting}
									options={accountOptions}
									value={accountOptions.find((acc) => acc.value === costAccount)}
									onChange={(option) => option && setCostAccount(option.value)}
									className="mb-3"
								/>
							</Col>
							<Col>
								<h4>Tracking Categories</h4>
								<label>Category</label>
								<Select
									isDisabled={!editing || submitting}
									options={trackingCategories}
									value={trackingCategories.find((tc) => tc.value === trackingCategory)}
									onChange={(option) => option && setTrackingCategory(option.value)}
									className="mb-3"
								/>
								{props.directors.map((dir) => (
									<React.Fragment key={dir.staffID}>
										<label>{dir.name}</label>
										<Select
											isDisabled={!editing || submitting}
											options={trackingCategoryOptions}
											value={trackingCategoryOptions?.find(
												(tc) =>
													tc.value === staffTrackingCategory.find((stc) => stc.staffID === dir.staffID)?.xeroTrackingCategoryOptionID
											)}
											onChange={(option) => {
												if (option) {
													const index = staffTrackingCategory.findIndex(
														(stc) => stc.staffID === dir.staffID || stc.billySettingsXeroStaffTrackingCategoryID === null
													)
													if (index < 0) {
														setStaffTrackingCategory([
															...staffTrackingCategory,
															{
																xeroTrackingCategoryOptionID: option.value,
																staffID: dir.staffID,
																billySettingsXeroStaffTrackingCategoryID: '',
																xeroTrackingCategoryOptionName: '',
															},
														])
													} else {
														setStaffTrackingCategory(
															staffTrackingCategory.map((stc, ind) =>
																ind === index ? { ...stc, xeroTrackingCategoryOptionID: option.value } : stc
															)
														)
													}
												}
											}}
											className="mb-3"
										/>
									</React.Fragment>
								))}
							</Col>
						</Row>
					)}
					<Row>
						<Col></Col>
						{editing && (
							<Col xs="auto">
								<Button className="w-100 mt-3" variant="secondary" disabled={submitting} onClick={handleCancel}>
									Cancel
								</Button>
							</Col>
						)}
						<Col xs="auto">
							{!editing && (
								<Button className="w-100 mt-3" onClick={() => setEditing(true)}>
									Edit Tracking Categories and Accounts
								</Button>
							)}
							{editing && (
								<Button className="w-100 mt-3" disabled={submitting} onClick={handleSave}>
									Save
								</Button>
							)}
						</Col>
					</Row>
				</>
			)}
		</Card>
	)
}

const OrgCard = (props: OrgCardProps) => {
	return (
		<Row>
			<Col>{props.org.name}</Col>
			<Col xs="auto">
				{props.org.authStatus ? (
					<Badge bg="info" className="mt-3">
						OK
					</Badge>
				) : (
					<Badge bg="secondary" className="clickable-icon mt-3">
						<a href={props.consentUrl} style={{ color: 'inherit' }}>
							Re-auth needed
						</a>
					</Badge>
				)}
			</Col>
			<Col xs="auto">
				{props.disconnecting ? (
					<Spinner animation={'border'} size={'sm'} style={{ marginLeft: '5px' }} />
				) : (
					<img src={XeroDisconnect} alt="Disconnect from Xero" onClick={() => props.updateShowDialog(props.org.connectionID)} />
				)}
			</Col>
		</Row>
	)
}

export { OrgCard, AdvancedOrgCard }
